import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";

/* ─── avatar initials helpers ─────────────────────────────────── */
const AVATAR_SEEDS = [
  { bg: "#f0edf7", color: "#4d4467" },
  { bg: "#fff8e8", color: "#b87600" },
  { bg: "#edf7f0", color: "#417505" },
  { bg: "#f0f4f8", color: "#475569" },
  { bg: "#fde8eb", color: "#d0021b" },
  { bg: "#f5f7f8", color: "#3a3b3f" },
];

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "?"
  );
}

function seedColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return AVATAR_SEEDS[h % AVATAR_SEEDS.length];
}

/* ─── state configs ───────────────────────────────────────────── */
const STATE_CFG = {
  success: {
    heroFrom: "#3a3152",
    hereTo: "#1e1b2e",
    glow: "rgba(77,68,103,0.35)",
    accent: "#f6a51f",
    badgeBg: "#417505",
    badgeBorder: "#1e1b2e",
    statusColor: "#6dbe2e",
    statusLabel: "Check-in Successful",
    confirmCls: "ctf-ci-btn--primary",
    confirmLabel: "Confirm ✓",
  },

  "No Active Membership": {
    heroFrom: "#5a3a00",
    hereTo: "#2e1d00",
    glow: "rgba(246,165,31,0.28)",
    accent: "#f6a51f",
    badgeBg: "#f6a51f",
    badgeBorder: "#2e1d00",
    statusColor: "#f6a51f",
    statusLabel: "No Active Membership",
    confirmCls: "ctf-ci-btn--warn",
    confirmLabel: "OK, Dismiss",
  },

  "Member already checked-in Today": {
    heroFrom: "#1b2d4a",
    hereTo: "#0d1826",
    glow: "rgba(71,85,105,0.35)",
    accent: "#475569",
    badgeBg: "#475569",
    badgeBorder: "#0d1826",
    statusColor: "#94a3b8",
    statusLabel: "Already Checked In Today",
    confirmCls: "ctf-ci-btn--blue",
    confirmLabel: "Got It",
  },

  "Member not found": {
    heroFrom: "#4a0a0f",
    hereTo: "#26050a",
    glow: "rgba(208,2,27,0.25)",
    accent: "#d0021b",
    badgeBg: "#d0021b",
    badgeBorder: "#26050a",
    statusColor: "#f87171",
    statusLabel: "Member Not Found",
    confirmCls: "ctf-ci-btn--danger",
    confirmLabel: "OK, Dismiss",
  },
};

function getCfg(member) {
  if (member.success) return STATE_CFG.success;
  return STATE_CFG[member.error] || STATE_CFG["Member not found"];
}

/* ─── image zoom hook ─────────────────────────────────────────── */
function useImageZoom() {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.6, 4));

  const zoomOut = () => {
    setZoom((z) => {
      const next = Math.max(z - 0.6, 1);
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  function onMouseDown(e) {
    if (zoom <= 1) return;
    setDragging(true);
    dragStart.current = {
      mx: e.clientX,
      my: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  }

  function onMouseMove(e) {
    if (!dragging || !dragStart.current) return;

    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.mx),
      y: dragStart.current.oy + (e.clientY - dragStart.current.my),
    });
  }

  function onMouseUp() {
    setDragging(false);
  }

  return {
    zoom,
    offset,
    dragging,
    zoomIn,
    zoomOut,
    reset,
    onMouseDown,
    onMouseMove,
    onMouseUp,
  };
}

/* ─── component ───────────────────────────────────────────────── */

export default function CheckinSuccessModal({ member, onClose }) {
  const [visible, setVisible] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const imgZoom = useImageZoom();

  useEffect(() => {
    if (member) setVisible(true);
    else setVisible(false);
  }, [member]);

  useEffect(() => {
    if (!member) setLightbox(false);
  }, [member]);

  useEffect(() => {
    if (member?.success) {
      const timer = setTimeout(() => handleDismiss(), 5000);
      return () => clearTimeout(timer);
    }
  }, [member]);

  if (!member) return null;

  const cfg = getCfg(member);
  const isSuccess = member.success;
  const initials = getInitials(member.name);
  const avColor = seedColor(member.name);

  function handleDismiss() {
    setVisible(false);
    setLightbox(false);
    setTimeout(onClose, 250);
  }

  function openLightbox() {
    if (member.image) {
      imgZoom.reset();
      setLightbox(true);
    }
  }

  return (
    <>
      {/* LIGHTBOX */}
      {lightbox && member.image && (
        <div className="ctf-ci-lightbox">
          <button
            className="ctf-ci-lightbox__close"
            onClick={() => setLightbox(false)}
          >
            ✕
          </button>

          <div
            className={`ctf-ci-lightbox__img-wrap ${
              imgZoom.dragging ? "ctf-ci-lightbox__img-wrap--dragging" : ""
            }`}
            onMouseDown={imgZoom.onMouseDown}
            onMouseMove={imgZoom.onMouseMove}
            onMouseUp={imgZoom.onMouseUp}
            onMouseLeave={imgZoom.onMouseUp}
          >
            <img
              src={member.image}
              alt={member.name}
              className="ctf-ci-lightbox__img"
              style={{
                transform: `scale(${imgZoom.zoom}) translate(${imgZoom.offset.x}px, ${imgZoom.offset.y}px)`,
              }}
              draggable={false}
            />
          </div>

          <div className="ctf-ci-lightbox__toolbar">
            <button
              className="ctf-ci-lightbox__btn"
              onClick={imgZoom.zoomOut}
              disabled={imgZoom.zoom <= 1}
            >
              −
            </button>

            <span className="ctf-ci-lightbox__zoom-label">
              {Math.round(imgZoom.zoom * 100)}%
            </span>

            <button
              className="ctf-ci-lightbox__btn"
              onClick={imgZoom.zoomIn}
              disabled={imgZoom.zoom >= 4}
            >
              +
            </button>

            <button className="ctf-ci-lightbox__btn" onClick={imgZoom.reset}>
              Reset
            </button>
          </div>

          <p className="ctf-ci-lightbox__name">{member.name}</p>
        </div>
      )}

      {/* MODAL */}
      <Modal
        isOpen={visible}
        toggle={handleDismiss}
        centered
        backdrop={true}
        className="large-pop adding-popup"
      >
        {/* HERO */}
        <div
          className="ctf-ci-hero"
          style={{
            background: `linear-gradient(155deg, ${cfg.heroFrom}, ${cfg.hereTo})`,
          }}
        >
          <div
            className="ctf-ci-hero__glow"
            style={{
              background: `radial-gradient(circle, ${cfg.glow} 0%, transparent 70%)`,
            }}
          />

          <div className="ctf-ci-avatar-wrap">
            {member.image ? (
              <img
                src={member.image}
                alt={member.name}
                className="ctf-ci-avatar-img ctf-ci-avatar-img--zoomable"
                onClick={openLightbox}
              />
            ) : (
              <div
                className="ctf-ci-avatar-ph"
                style={{
                  background: avColor.bg,
                  color: avColor.color,
                }}
              >
                {initials}
              </div>
            )}
          </div>

          <h2 className="ctf-ci-member-name">
            {member.name || "Unknown Member"}
          </h2>

          {isSuccess && member.package && (
            <span className="ctf-ci-package-pill">{member.package}</span>
          )}

          <div
            className="ctf-ci-status-row"
            style={{ color: cfg.statusColor }}
          >
            <span
              className="ctf-ci-status-dot"
              style={{ background: cfg.statusColor }}
            />
            {cfg.statusLabel}
          </div>
        </div>

        <ModalBody className="ctf-ci-body">
          {isSuccess ? (
            <>
              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Check-in Time</span>
                <span className="ctf-ci-row__value ctf-ci-row__value--mono">
                  {member.time}
                </span>
              </div>

              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Package</span>
                <span className="ctf-ci-row__value">
                  {member.package || "—"}
                </span>
              </div>

              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Status</span>
                <span className="ctf-ci-row__value ctf-ci-row__value--active">
                  ● Active
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Scanned At</span>
                <span className="ctf-ci-row__value ctf-ci-row__value--mono">
                  {member.time}
                </span>
              </div>

              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Member</span>
                <span className="ctf-ci-row__value">
                  {member.name || "—"}
                </span>
              </div>

              <div className="ctf-ci-row">
                <span className="ctf-ci-row__label">Reason</span>
                <span className="ctf-ci-row__value ctf-ci-tag--danger">
                  {member.error}
                </span>
              </div>
            </>
          )}
        </ModalBody>

        <ModalFooter className="ctf-ci-footer">
          <button
            className="ctf-ci-btn ctf-ci-btn--ghost"
            onClick={handleDismiss}
          >
            Dismiss
          </button>

          <button
            className={`ctf-ci-btn ${cfg.confirmCls}`}
            onClick={handleDismiss}
          >
            {cfg.confirmLabel}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
}